import { Injectable } from '@angular/core';
import {
  CancelScheduledChangeRequest,
  SubscriptionChangeRequest
} from '../../state/actions/organizations/subscription.actions';

type SubscriptionMutationRequest = SubscriptionChangeRequest | CancelScheduledChangeRequest;
type SubscriptionMutationKind = 'change' | 'cancel-scheduled';

interface PersistedSubscriptionMutation {
  version: 1;
  key: string;
  kind: SubscriptionMutationKind;
  organizationId: string;
  fingerprint: string;
  mutationId: string;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionMutationStateService {
  private readonly storageKey = 'apidef.subscription-mutations.v1';
  private readonly memoryEntries = new Map<string, PersistedSubscriptionMutation>();

  getOrCreateChange(request: SubscriptionChangeRequest): string {
    return this.getOrCreate('change', request.organizationId, {
      planId: request.planId,
      paymentInterval: request.paymentInterval
    });
  }

  getOrCreateScheduledCancellation(request: CancelScheduledChangeRequest): string {
    return this.getOrCreate('cancel-scheduled', request.organizationId, {});
  }

  clearChange(request: SubscriptionChangeRequest): void {
    this.remove(this.createKey('change', request.organizationId, this.changeFingerprint(request)));
  }

  clearScheduledCancellation(request: CancelScheduledChangeRequest): void {
    this.remove(this.createKey('cancel-scheduled', request.organizationId, '{}'));
  }

  clearAuthoritativelyCompletedCheckout(mutationId: string): void {
    this.writeEntries(this.readEntries().filter((entry) => entry.kind !== 'change' || entry.mutationId !== mutationId));
  }

  reset(request: SubscriptionMutationRequest): void {
    if ('planId' in request) {
      this.clearChange(request);
    } else {
      this.clearScheduledCancellation(request);
    }
  }

  private getOrCreate(
    kind: SubscriptionMutationKind,
    organizationId: string,
    fingerprintFields: Record<string, string>
  ): string {
    const fingerprint = JSON.stringify(fingerprintFields);
    const key = this.createKey(kind, organizationId, fingerprint);
    const entries = this.readEntries();
    const existing = entries.find((entry) => entry.key === key);
    if (existing) {
      return existing.mutationId;
    }

    const entry: PersistedSubscriptionMutation = {
      version: 1,
      key,
      kind,
      organizationId,
      fingerprint,
      mutationId: crypto.randomUUID(),
      createdAt: new Date().toISOString()
    };
    this.writeEntries([...entries, entry]);
    return entry.mutationId;
  }

  private changeFingerprint(request: SubscriptionChangeRequest): string {
    return JSON.stringify({ planId: request.planId, paymentInterval: request.paymentInterval });
  }

  private createKey(kind: SubscriptionMutationKind, organizationId: string, fingerprint: string): string {
    return `${kind}:${organizationId}:${fingerprint}`;
  }

  private remove(key: string): void {
    this.writeEntries(this.readEntries().filter((entry) => entry.key !== key));
  }

  private readEntries(): PersistedSubscriptionMutation[] {
    try {
      const serialized = sessionStorage.getItem(this.storageKey);
      if (!serialized) {
        return [...this.memoryEntries.values()];
      }
      const parsed: unknown = JSON.parse(serialized);
      if (!Array.isArray(parsed)) {
        sessionStorage.removeItem(this.storageKey);
        return [...this.memoryEntries.values()];
      }
      const valid = parsed.filter((entry): entry is PersistedSubscriptionMutation => this.isValidEntry(entry));
      if (valid.length !== parsed.length) {
        this.writeEntries(valid);
      }
      return valid;
    } catch {
      return [...this.memoryEntries.values()];
    }
  }

  private writeEntries(entries: PersistedSubscriptionMutation[]): void {
    this.memoryEntries.clear();
    entries.forEach((entry) => this.memoryEntries.set(entry.key, entry));
    try {
      if (entries.length) {
        sessionStorage.setItem(this.storageKey, JSON.stringify(entries));
      } else {
        sessionStorage.removeItem(this.storageKey);
      }
    } catch {
      // The in-memory copy remains available when storage is disabled or unavailable.
    }
  }

  private isValidEntry(value: unknown): value is PersistedSubscriptionMutation {
    if (!value || typeof value !== 'object') return false;
    const entry = value as Partial<PersistedSubscriptionMutation>;
    if (entry.version !== 1 || (entry.kind !== 'change' && entry.kind !== 'cancel-scheduled') ||
        typeof entry.organizationId !== 'string' || !entry.organizationId ||
        typeof entry.fingerprint !== 'string' || typeof entry.mutationId !== 'string' || !entry.mutationId ||
        typeof entry.createdAt !== 'string' || Number.isNaN(Date.parse(entry.createdAt))) {
      return false;
    }
    return entry.key === this.createKey(entry.kind, entry.organizationId, entry.fingerprint);
  }
}
